import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import type { GeneralFormProps } from '@/types/general';

import { Checkbox } from '../ui/checkbox';

type RHFShadcnCheckboxArrayProps = {
  name: string;
  value: string; // The value to add/remove from the array
  title: string;
  description?: string;
  disabled?: boolean;
  required?: boolean;
  onChange?: (checked: boolean, value: string) => void;
  className?: string;
} & Omit<GeneralFormProps, 'value' | 'onChange'>;

function RHFShadcnCheckboxArray({
  name,
  title,
  description,
  value,
  required,
  onChange: externalOnChange,
  disabled,
  className,
}: RHFShadcnCheckboxArrayProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const currentValue = field.value as string[] || [];
        const isChecked = currentValue.includes(value);

        const handleChange = (checked: boolean) => {
          let newValue: string[];

          if (checked) {
            // Add the value to the array if not already present
            newValue = currentValue.includes(value)
              ? currentValue
              : [...currentValue, value];
          } else {
            // Remove the value from the array
            newValue = currentValue.filter(item => item !== value);
          }

          if (externalOnChange) {
            externalOnChange(checked, value);
          } else {
            field.onChange(newValue);
          }
        };

        return (
          <FormItem className={`flex flex-row items-start gap-3 w-full ${className || ''}`}>
            <FormControl>
              <Checkbox
                disabled={disabled}
                required={required}
                data-testid={`${field.name}-${value}`}
                className={`mt-1 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                onCheckedChange={handleChange}
                checked={isChecked}
              />
            </FormControl>
            <div
              className={`flex-1 pb-2 leading-none ${
                disabled ? 'text-muted-foreground' : ''
              }`}
            >
              <FormLabel
                className={`block ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {title}
              </FormLabel>
              {description && <FormDescription className="mt-1">{description}</FormDescription>}
            </div>
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnCheckboxArray;
